import { isAssessmentSubtask } from './contentGenerationSubtasks';

export const MODULE_EXAM_TASK_CODE = '__MODULE_EXAM__';
export const PROGRAM_EXAM_TASK_CODE = '__PROGRAM_EXAM__';

/**
 * @param {{ task_code?: string, config?: object }} row
 */
export function isProgramExamAssessment(row) {
  const tc = row && row.task_code != null ? String(row.task_code) : '';
  if (tc === PROGRAM_EXAM_TASK_CODE) return true;
  const cfg = row && row.config;
  if (cfg && typeof cfg === 'object' && cfg.scope === 'program') return true;
  return false;
}

/**
 * @param {{ task_code?: string, config?: object }} row
 */
export function isModuleExamAssessment(row) {
  if (isProgramExamAssessment(row)) return false;
  const tc = row && row.task_code != null ? String(row.task_code) : '';
  if (tc === MODULE_EXAM_TASK_CODE) return true;
  const cfg = row && row.config;
  if (cfg && typeof cfg === 'object' && cfg.scope === 'module') return true;
  return false;
}

export function filterTaskScopedAssessments(assessments) {
  return (assessments || []).filter(
    (a) => !isModuleExamAssessment(a) && !isProgramExamAssessment(a)
  );
}

export function filterModuleExamAssessments(assessments) {
  return (assessments || []).filter((a) => isModuleExamAssessment(a));
}

export function filterProgramExamAssessments(assessments) {
  return (assessments || []).filter((a) => isProgramExamAssessment(a));
}

/**
 * Union of IAC subtasks across all ELO modules in assignedItems.
 * @param {Record<string, { KT?: object[], PS?: object[] }>} assignedItems
 */
export function getExpectedIacsForQualification(assignedItems) {
  const seen = new Set();
  const out = [];
  for (const moduleId of Object.keys(assignedItems || {})) {
    for (const i of getExpectedIacsForModule(moduleId, assignedItems)) {
      if (seen.has(i.code)) continue;
      seen.add(i.code);
      out.push(i);
    }
  }
  return out.sort((a, b) => String(a.code).localeCompare(String(b.code)));
}

/** KT/PS parent rows for an ELO (same source as task cards). */
export function getModuleTaskSpecs(moduleId, assignedItems) {
  const m = assignedItems[moduleId] || { KT: [], PS: [] };
  return [...(m.KT || []), ...(m.PS || [])].map((t) => ({
    code: t.code,
    title: t.title || t.code
  }));
}

/** All KT/PS parent tasks across ELO modules (for program exam source pickers). */
export function getAllTaskSpecs(assignedItems) {
  const seen = new Set();
  const out = [];
  for (const moduleId of Object.keys(assignedItems || {})) {
    for (const t of getModuleTaskSpecs(moduleId, assignedItems)) {
      if (seen.has(t.code)) continue;
      seen.add(t.code);
      out.push(t);
    }
  }
  return out.sort((a, b) => String(a.code).localeCompare(String(b.code)));
}

/** Union of question.task_code values from stored assessments (e.g. module exams). */
export function collectTaskCodesFromAssessmentQuestions(assessments) {
  const s = new Set();
  for (const row of assessments || []) {
    const qs = row?.payload?.questions;
    if (!Array.isArray(qs)) continue;
    for (const q of qs) {
      const tc = q.task_code != null ? String(q.task_code).trim() : '';
      if (tc) s.add(tc);
    }
  }
  return s;
}

/**
 * Expected IAC nodes for an ELO module from assigned KT/PS trees.
 * @param {number|string} moduleId
 * @param {Record<string, { KT?: object[], PS?: object[] }>} assignedItems
 * @returns {Array<{ code: string, title: string, task_code: string, task_title: string }>}
 */
export function getExpectedIacsForModule(moduleId, assignedItems) {
  const m = assignedItems[moduleId] || { KT: [], PS: [] };
  const items = [...(m.KT || []), ...(m.PS || [])];
  const out = [];
  for (const item of items) {
    for (const ch of item.children || []) {
      if (isAssessmentSubtask(ch)) {
        out.push({
          code: ch.code,
          title: ch.title || ch.code,
          task_code: item.code,
          task_title: item.title || item.code
        });
      }
    }
  }
  return out;
}

/**
 * @param {Array<{ payload?: object }>} assessments
 * @returns {string[]}
 */
export function collectAddressedIacCodesFromAssessments(assessments) {
  const set = new Set();
  if (!Array.isArray(assessments)) return [];
  for (const row of assessments) {
    const payload = row && row.payload;
    if (!payload || typeof payload !== 'object') continue;
    const questions = payload.questions;
    if (!Array.isArray(questions)) continue;
    for (const q of questions) {
      const fromArray = Array.isArray(q.iac_codes)
        ? q.iac_codes
        : q.assessment_code
          ? [q.assessment_code]
          : [];
      for (const c of fromArray) {
        if (typeof c === 'string' && c.trim()) set.add(c.trim());
      }
    }
  }
  return [...set].sort();
}

/**
 * Count IAC occurrences across assessments (union of all questions).
 * @param {Array<{ id?: number, payload?: { questions?: object[] } }>} assessments
 * @returns {Array<{ code: string, count: number, questionIds: string[], assessmentIds: number[] }>}
 */
export function countIacOccurrences(assessments) {
  const map = new Map();
  for (const row of assessments || []) {
    const aid = row.id != null ? row.id : null;
    const qs = row?.payload?.questions;
    if (!Array.isArray(qs)) continue;
    qs.forEach((q, qIdx) => {
      const qid = q.id || `q${qIdx + 1}`;
      const codes = Array.isArray(q.iac_codes)
        ? q.iac_codes
        : q.assessment_code
          ? [q.assessment_code]
          : [];
      for (const c of codes) {
        const code = typeof c === 'string' ? c.trim() : String(c || '').trim();
        if (!code) continue;
        if (!map.has(code)) {
          map.set(code, { code, count: 0, questionIds: [], assessmentIds: [] });
        }
        const entry = map.get(code);
        entry.count += 1;
        if (!entry.questionIds.includes(qid)) entry.questionIds.push(qid);
        if (aid != null && !entry.assessmentIds.includes(aid)) entry.assessmentIds.push(aid);
      }
    });
  }
  return [...map.values()].sort((a, b) => String(a.code).localeCompare(String(b.code)));
}

/** @param {Array<{ code: string, count: number }>} counts @param {number} [threshold=2] */
export function findOverAssessedIacs(counts, threshold = 2) {
  return (counts || []).filter((c) => c.count >= threshold);
}

/** @param {Array<{ code: string, count: number }>} counts @param {number} [threshold=3] */
export function findRepeatedIacTriples(counts, threshold = 3) {
  return (counts || []).filter((c) => c.count >= threshold);
}

/** @param {Array<{ code: string, task_code: string, task_title: string }>} expectedIacs */
export function groupExpectedIacsByTask(expectedIacs) {
  const groups = new Map();
  for (const i of expectedIacs || []) {
    const key = i.task_code || 'unknown';
    if (!groups.has(key)) {
      groups.set(key, { task_code: key, task_title: i.task_title || key, iacs: [] });
    }
    groups.get(key).iacs.push(i);
  }
  return [...groups.values()].sort((a, b) =>
    String(a.task_code).localeCompare(String(b.task_code))
  );
}

const PERSONAL_DATA_PATTERNS = [
  /\byour own\b/i,
  /\byour personal\b/i,
  /\byour financial\b/i,
  /\byour bank\b/i,
  /\byour salary\b/i,
  /\byour income\b/i,
  /\byour budget\b/i,
  /\byour household\b/i
];

/** @param {object[]} questions */
export function flagPersonalDataQuestions(questions) {
  const out = [];
  if (!Array.isArray(questions)) return out;
  questions.forEach((q, idx) => {
    const text = [q.scenario, q.question].filter(Boolean).join(' ');
    if (!text) return;
    for (const pat of PERSONAL_DATA_PATTERNS) {
      if (pat.test(text)) {
        out.push({
          question_id: q.id || `q${idx + 1}`,
          question_index: idx + 1,
          matched_phrase: String(pat)
        });
        break;
      }
    }
  });
  return out;
}

function tokenize(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter((w) => w.length > 1);
}

function wordSet(text) {
  return new Set(tokenize(text));
}

function jaccard(setA, setB) {
  if (!setA.size && !setB.size) return 1;
  let inter = 0;
  for (const w of setA) {
    if (setB.has(w)) inter += 1;
  }
  const union = setA.size + setB.size - inter;
  return union ? inter / union : 0;
}

const QUESTION_SIMILARITY_THRESHOLD = 0.82;

/** @param {object[]} questions */
export function findDuplicateQuestionPairs(questions, threshold = QUESTION_SIMILARITY_THRESHOLD) {
  const pairs = [];
  const qs = questions || [];
  for (let i = 0; i < qs.length; i++) {
    const textA = [qs[i].scenario, qs[i].question].filter(Boolean).join(' ');
    if (tokenize(textA).length < 8) continue;
    const setA = wordSet(textA);
    for (let j = i + 1; j < qs.length; j++) {
      const textB = [qs[j].scenario, qs[j].question].filter(Boolean).join(' ');
      if (tokenize(textB).length < 8) continue;
      const sim = jaccard(setA, wordSet(textB));
      if (sim >= threshold) {
        pairs.push({
          indexA: i,
          indexB: j,
          idA: qs[i].id || `q${i + 1}`,
          idB: qs[j].id || `q${j + 1}`,
          similarity: Math.round(sim * 1000) / 1000
        });
      }
    }
  }
  return pairs;
}
